import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { computeMinDimensions, IataSourceLink } from '../../data/iataRules';

const PET_FIELDS = ['a', 'b', 'c', 'd', 'bedding'];
const CRATE_FIELDS = ['crate_length', 'crate_width', 'crate_height'];
const IN_TO_CM = 2.54;

export const EMPTY_MEASUREMENTS = {
  unit: 'cm', a: '', b: '', c: '', d: '', bedding: '', crate_length: '', crate_width: '', crate_height: '',
  brachycephalic: false, animals: 1,
};

export const measurementsComplete = (m) =>
  [...PET_FIELDS, ...CRATE_FIELDS].every((k) => m[k] !== '' && Number(m[k]) >= 0) &&
  [...PET_FIELDS.filter((k) => k !== 'bedding'), ...CRATE_FIELDS].every((k) => Number(m[k]) > 0);

export const toCmPayload = (m) => {
  const f = m.unit === 'in' ? IN_TO_CM : 1;
  const out = { brachycephalic: m.brachycephalic, animals: Number(m.animals) };
  [...PET_FIELDS, ...CRATE_FIELDS].forEach((k) => { out[k] = Math.round(Number(m[k]) * f * 10) / 10; });
  return out;
};

const NumField = ({ id, label, value, onChange }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-medium text-slate-700 mb-1 block">{label}</Label>
    <Input id={id} data-testid={`measure-${id}`} type="number" min="0" step="0.1" inputMode="decimal"
      value={value} onChange={(e) => onChange(e.target.value)} className="h-10" />
  </div>
);

export function MeasurementsForm({ value, onChange }) {
  const { t, i18n } = useTranslation();
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const ready = measurementsComplete(value);
  const min = ready ? computeMinDimensions(toCmPayload(value)) : null;

  return (
    <div data-testid="measurements-form" className="rounded-xl border border-slate-200 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">{t('measure.title')}</p>
        <select data-testid="measure-unit" value={value.unit} onChange={(e) => set('unit')(e.target.value)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1">
          <option value="cm">cm</option>
          <option value="in">in</option>
        </select>
      </div>
      <p className="text-xs text-slate-500">{t('measure.intro')}</p>
      <div className="grid grid-cols-2 gap-3">
        {PET_FIELDS.map((k) => <NumField key={k} id={k} label={t(`measure.${k}`)} value={value[k]} onChange={set(k)} />)}
        <div>
          <Label htmlFor="animals" className="text-xs font-medium text-slate-700 mb-1 block">{t('measure.animals')}</Label>
          <select id="animals" data-testid="measure-animals" value={value.animals} onChange={(e) => set('animals')(Number(e.target.value))}
            className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm">
            {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-700">
        <input type="checkbox" data-testid="measure-brachycephalic" checked={value.brachycephalic}
          onChange={(e) => set('brachycephalic')(e.target.checked)} />
        {t('measure.brachycephalic')}
      </label>
      <p className="text-xs font-semibold text-slate-900 pt-2">{t('measure.crate_title')}</p>
      <div className="grid grid-cols-3 gap-3">
        {CRATE_FIELDS.map((k) => <NumField key={k} id={k} label={t(`measure.${k}`)} value={value[k]} onChange={set(k)} />)}
      </div>
      {min && (
        <p data-testid="measure-min-result" className="text-xs text-slate-700 bg-slate-50 rounded-md p-2">
          {t('measure.min_result')}: {min.length} × {min.width} × {min.height} cm
        </p>
      )}
      <IataSourceLink lang={i18n.language} />
    </div>
  );
}
